import { ChangeEvent, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FileText, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { WizardValues } from "./schema";

const DOC_TYPES = [
  "GST_CERTIFICATE",
  "PAN_CARD",
  "FSSAI_LICENSE",
  "TRADE_LICENSE",
  "SHOP_ESTABLISHMENT",
  "MEDICAL_REGISTRATION",
  "OTHER",
];

interface RowState {
  file: File | null;
  docType: string;
  label: string;
  docNumber: string;
  expiryDate: string;
}

const emptyRow = (): RowState => ({
  file: null,
  docType: DOC_TYPES[0],
  label: "",
  docNumber: "",
  expiryDate: "",
});

export const PendingDocumentsList = () => {
  const { watch, setValue } = useFormContext<WizardValues>();
  const queued = watch("pendingDocuments") ?? [];
  const [row, setRow] = useState<RowState>(emptyRow());

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    setRow((r) => ({ ...r, file: e.target.files?.[0] ?? null }));
  };

  const addRow = () => {
    if (!row.file) return;
    const next = [
      ...queued,
      {
        file: row.file,
        meta: {
          docType: row.docType,
          label: row.label || undefined,
          docNumber: row.docNumber || undefined,
          expiryDate: row.expiryDate || undefined,
        },
      },
    ];
    setValue("pendingDocuments", next, { shouldValidate: true });
    setRow(emptyRow());
  };

  const remove = (i: number) => {
    setValue(
      "pendingDocuments",
      queued.filter((_, idx) => idx !== i),
      { shouldValidate: true },
    );
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Upload documents</Label>
        <p className="text-xs text-muted-foreground">
          Optional at creation — required before approval. Add one at a time.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-2 p-3 border border-dashed border-border rounded-card">
        <div>
          <Label htmlFor="docFile" className="text-xs">
            File
          </Label>
          <Input id="docFile" type="file" onChange={onFile} />
        </div>
        <div>
          <Label className="text-xs">Document type</Label>
          <Select
            value={row.docType}
            onValueChange={(v) => setRow((r) => ({ ...r, docType: v }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t.replaceAll("_", " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Label (optional)"
          value={row.label}
          onChange={(e) => setRow((r) => ({ ...r, label: e.target.value }))}
        />
        <Input
          placeholder="Doc number (optional)"
          value={row.docNumber}
          onChange={(e) => setRow((r) => ({ ...r, docNumber: e.target.value }))}
        />
        <Input
          type="date"
          value={row.expiryDate}
          onChange={(e) =>
            setRow((r) => ({ ...r, expiryDate: e.target.value }))
          }
        />
        <div className="flex items-end">
          <Button
            type="button"
            onClick={addRow}
            disabled={!row.file}
            className="w-full"
            variant="outline"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Queue document
          </Button>
        </div>
      </div>

      {queued.length > 0 ? (
        <ul className="divide-y divide-border border border-border rounded-card">
          {queued.map((d, i) => (
            <li key={i} className="flex items-center gap-3 p-2 text-sm">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {(d.file as File).name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {d.meta.docType}
                  {d.meta.label ? ` · ${d.meta.label}` : ""}
                  {d.meta.docNumber ? ` · #${d.meta.docNumber}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
